import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft, BadgePercent, BadgeIndianRupee, FileText, Pencil, Plus, Trash2, Search,
  Banknote, Receipt, TrendingUp, Wallet, Activity, ArrowUpDown, BarChart3, Table as TableIcon, PieChart as PieIcon,
  UserRound, Stethoscope, Crown, BadgeCheck,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { useKeyNav } from "@/lib/useKeyNav";
import { B2BRevenueTile } from "@/pages/admin/B2B";

const CHART_COLORS = ["#185FA5", "#1D9E75", "#E0A82E", "#A23D2C", "#604096", "#0D8AB7", "#3DAA82", "#D87B33", "#7A4A95"];

function ChartToggle({ view, setView }) {
  return (
    <div className="biz-view-toggle" role="tablist" data-testid="biz-view-toggle">
      <button className={view === "table" ? "on" : ""} onClick={() => setView("table")} data-testid="biz-view-table"><TableIcon size={13} /> Table</button>
      <button className={view === "bar" ? "on" : ""} onClick={() => setView("bar")} data-testid="biz-view-bar"><BarChart3 size={13} /> Bar chart</button>
      <button className={view === "pie" ? "on" : ""} onClick={() => setView("pie")} data-testid="biz-view-pie"><PieIcon size={13} /> Pie chart</button>
    </div>
  );
}

function ChartFrame({ children, title }) {
  return (
    <div className="biz-chart-frame">
      {title && <h4 className="biz-section-h" style={{ marginBottom: 12 }}>{title}</h4>}
      <div style={{ width: "100%", height: 360 }}>
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

const inr = (n) => {
  const v = Number(n || 0);
  return "₹" + v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

/* ============================================================
   BUSINESS · PATIENTS
   ============================================================ */
export function BusinessPatients() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editAppt, setEditAppt] = useState(null);
  const [editProfile, setEditProfile] = useState(false);
  const [view, setView] = useState("table");
  const suggestList = q.trim() === "" ? [] : rows.slice(0, 10);
  const { activeIndex, setActiveIndex, onKeyDown, listRef } = useKeyNav(suggestList, (p) => openPatient(p), { onEscape: () => setQ("") });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/business/patients", { params: q ? { q } : {} });
      setRows(data.patients || []);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { const t = setTimeout(load, 220); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const openPatient = async (p) => {
    setActive(p);
    try {
      const { data } = await api.get(`/admin/business/patient/${p.id}`);
      setDetail(data);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); setDetail(null); }
  };

  const totals = useMemo(() => rows.reduce((acc, r) => ({
    appts: acc.appts + r.total_appointments,
    paid: acc.paid + r.total_paid,
    disc: acc.disc + r.total_discount,
  }), { appts: 0, paid: 0, disc: 0 }), [rows]);

  if (active && detail) {
    return (
      <div className="panel">
        <button className="back" onClick={() => { setActive(null); setDetail(null); }} data-testid="biz-patient-back">
          <ArrowLeft size={15} /> Back to patients
        </button>
        <div className="biz-detail-head">
          <div>
            <h3>{detail.patient.name}</h3>
            <p className="sub">{detail.patient.patient_code} · {detail.patient.email || "—"} · {detail.patient.mobile || "—"}</p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditProfile(true)} data-testid="biz-patient-edit" style={{ alignSelf: "flex-start" }}><Pencil size={13} /> Edit details</button>
        </div>
        <div className="biz-detail-stats" style={{ marginBottom: 4 }}>
          <div><div className="n">{detail.appointments.length}</div><div className="l">Appointments</div></div>
          <div><div className="n">{inr(detail.appointments.filter(a => a.paid && a.status !== "cancelled").reduce((s, a) => s + a.net_paid, 0))}</div><div className="l">Net paid</div></div>
          <div><div className="n">{inr(detail.appointments.reduce((s, a) => s + (a.discount || 0), 0))}</div><div className="l">Discounts</div></div>
        </div>
        <div className="biz-status-badges" data-testid="biz-patient-status-badges">
          {["completed", "upcoming", "pending", "missed"].map(s => (
            <div className={`biz-status-badge ${s}`} key={s} data-testid={`biz-patient-status-${s}`}>
              <span className="n">{(detail.status_counts || {})[s] || 0}</span>
              <span className="l">{s === "pending" ? "Pending (dr no-show)" : s === "missed" ? "Missed (pt no-show)" : s}</span>
            </div>
          ))}
        </div>
        <table data-testid="biz-patient-appts">
          <thead><tr><th>Date</th><th>Doctor</th><th>Service</th><th>Status</th><th>Fee</th><th>Discount</th><th>Net</th><th>Action</th></tr></thead>
          <tbody>{detail.appointments.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", padding: "24px 0", color: "var(--slate)" }}>No appointments yet.</td></tr>}{detail.appointments.map(a => (
            <tr key={a.id} data-testid={`biz-patient-appt-${a.id}`}>
              <td>{fmtDate(a.date)} <span style={{ color: "var(--slate)", fontSize: 12 }}>{a.time}</span></td>
              <td>{a.doctor_name}<br /><small style={{ color: "var(--slate)" }}>{a.specialty}</small></td>
              <td>{a.consult_type}</td>
              <td><span className={`pill-mini ${a.status}`}>{a.status}</span></td>
              <td>{inr(a.fee)}</td>
              <td>{a.discount ? <span title={a.discount_reason || ""} style={{ color: "var(--green-deep)" }}>−{inr(a.discount)}</span> : "—"}</td>
              <td><b>{inr(a.net_paid)}</b></td>
              <td><button className="btn btn-ghost btn-sm" onClick={() => setEditAppt(a)} data-testid={`biz-patient-discount-${a.id}`}><BadgePercent size={13} /> Discount</button></td>
            </tr>
          ))}</tbody>
        </table>
        {editAppt && (
          <DiscountModal appt={editAppt} onClose={() => setEditAppt(null)} onSaved={() => { setEditAppt(null); openPatient(active); load(); }} />
        )}
        {editProfile && (
          <PatientEditModal patient={detail.patient} onClose={() => setEditProfile(false)} onSaved={() => { setEditProfile(false); openPatient(active); load(); }} />
        )}
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <div><h3>Patient revenue</h3><p className="sub">{rows.length} patient{rows.length === 1 ? "" : "s"} · Click any row to see their full payment history and apply per-service discounts.</p></div>
      </div>
      <div className="biz-stat-row">
        <div className="biz-stat"><div className="n">{rows.length}</div><div className="l">Patients matched</div></div>
        <div className="biz-stat"><div className="n">{totals.appts}</div><div className="l">Total appointments</div></div>
        <div className="biz-stat green"><div className="n">{inr(totals.paid)}</div><div className="l">Lifetime revenue</div></div>
        <div className="biz-stat amber"><div className="n">{inr(totals.disc)}</div><div className="l">Discounts given</div></div>
      </div>
      <div className="biz-search-wrap">
        <div className="patient-search-bar" style={{ maxWidth: 640, margin: 0 }}>
          <Search size={15} className="patient-search-ic" />
          <input
            className="patient-search-input"
            placeholder="Search by name, Patient ID, mobile or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            aria-activedescendant={activeIndex >= 0 && suggestList[activeIndex] ? `biz-patient-suggest-${suggestList[activeIndex].id}` : undefined}
            role="combobox"
            aria-expanded={suggestList.length > 0}
            data-testid="biz-patients-search"
          />
          {q && <button className="patient-search-clear" onClick={() => setQ("")}>✕</button>}
        </div>
        {q.trim() !== "" && (
          <div className="biz-suggest" data-testid="biz-patients-suggest" role="listbox" ref={listRef}>
            {rows.length === 0 && <div className="biz-suggest-empty">No patients match “{q}”.</div>}
            {suggestList.map((p, i) => (
              <button
                key={p.id}
                type="button"
                id={`biz-patient-suggest-${p.id}`}
                role="option"
                aria-selected={i === activeIndex}
                className={`biz-suggest-row ${i === activeIndex ? "active" : ""}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => openPatient(p)}
                data-testid={`biz-patient-suggest-${p.id}`}
              >
                <span className="ib-avatar patient"><UserRound size={14} /></span>
                <span className="biz-suggest-main">
                  <b>{p.name}</b>
                  <small>{p.patient_code || "—"}{p.mobile ? " · " + p.mobile : ""}{p.email ? " · " + p.email : ""}</small>
                </span>
                <span className="biz-suggest-meta">{inr(p.total_paid)}</span>
              </button>
            ))}
            {rows.length > 10 && <div className="biz-suggest-more">+ {rows.length - 10} more · scroll the table below to see all matches</div>}
            {suggestList.length > 0 && <div className="biz-suggest-hint">↑ ↓ to move · ↵ to open · esc to clear</div>}
          </div>
        )}
      </div>
      <ChartToggle view={view} setView={setView} />
      {view === "table" && (
      <table data-testid="biz-patients-table">
        <thead><tr><th>Sl.</th><th>Patient ID</th><th>Name</th><th>Contact</th><th>Appts</th><th>Completed</th><th>Discount</th><th>Lifetime paid</th></tr></thead>
        <tbody>{rows.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", padding: "30px 0", color: "var(--slate)" }}>{q ? "No patients match the search." : "No patients yet."}</td></tr>}{rows.map((p, i) => (
          <tr key={p.id} className="row-click" onClick={() => openPatient(p)} data-testid={`biz-patient-row-${p.id}`}>
            <td>{i + 1}</td>
            <td><span className="pid-chip">{p.patient_code || "—"}</span></td>
            <td><b>{p.name}</b></td>
            <td><small style={{ color: "var(--slate)" }}>{p.mobile || ""}<br />{p.email || ""}</small></td>
            <td>{p.total_appointments}</td>
            <td>{p.completed}</td>
            <td>{p.total_discount ? inr(p.total_discount) : "—"}</td>
            <td><b>{inr(p.total_paid)}</b></td>
          </tr>
        ))}</tbody>
      </table>
      )}
      {view === "bar" && (() => {
        const data = [...rows].sort((a, b) => b.total_paid - a.total_paid).slice(0, 15).map(p => ({
          name: (p.name || "—").split(" ").slice(0, 2).join(" "), Paid: p.total_paid, Discount: p.total_discount,
        }));
        if (data.length === 0) return <div className="biz-chart-empty">No data to plot. Patients will appear here as soon as appointments are paid.</div>;
        return (
          <ChartFrame title="Top patients by lifetime revenue">
            <BarChart data={data} margin={{ top: 10, right: 16, bottom: 60, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6eef7" />
              <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={70} stroke="#6e8398" fontSize={12} />
              <YAxis stroke="#6e8398" fontSize={12} tickFormatter={(v) => "₹" + v} />
              <Tooltip formatter={(v) => inr(v)} />
              <Legend />
              <Bar dataKey="Paid" fill="#185FA5" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Discount" fill="#E0A82E" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ChartFrame>
        );
      })()}
      {view === "pie" && (() => {
        const total = rows.reduce((s, r) => s + r.total_paid, 0);
        if (total === 0) return <div className="biz-chart-empty">No revenue recorded yet.</div>;
        const data = [...rows].sort((a, b) => b.total_paid - a.total_paid).slice(0, 8).map(p => ({ name: p.name, value: p.total_paid }));
        const rest = rows.slice(8).reduce((s, r) => s + r.total_paid, 0);
        if (rest > 0) data.push({ name: "Others", value: rest });
        return (
          <ChartFrame title="Revenue share by patient (top 8 + others)">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={130} paddingAngle={2}>
                {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => inr(v)} />
              <Legend verticalAlign="bottom" />
            </PieChart>
          </ChartFrame>
        );
      })()}
    </div>
  );
}

function DiscountModal({ appt, onClose, onSaved }) {
  const [disc, setDisc] = useState(appt.discount || 0);
  const [reason, setReason] = useState(appt.discount_reason || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/business/appointment/${appt.id}/discount`, { discount: Number(disc) || 0, reason });
      toast("Discount saved ✓");
      onSaved();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-shade" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h3>Apply discount</h3>
        <p className="lead" style={{ marginTop: 0 }}>{fmtDate(appt.date)} · {appt.doctor_name} · {appt.consult_type}</p>
        <p className="lead">Original fee: <b>{inr(appt.fee)}</b></p>
        <label className="ib-field-label">Discount amount (₹)</label>
        <input type="number" className="ib-lookup-input" value={disc} onChange={(e) => setDisc(e.target.value)} min={0} max={appt.fee} data-testid="biz-discount-amount" />
        <label className="ib-field-label" style={{ marginTop: 10 }}>Reason (optional)</label>
        <input className="ib-lookup-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Loyalty waiver, hardship case…" data-testid="biz-discount-reason" />
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-green" onClick={save} disabled={busy} data-testid="biz-discount-save">{busy ? "Saving…" : "Save discount"}</button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   BUSINESS · DOCTORS
   ============================================================ */
export function BusinessDoctors() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editProfile, setEditProfile] = useState(false);
  const [view, setView] = useState("table");
  const suggestList = q.trim() === "" ? [] : rows.slice(0, 10);
  const { activeIndex, setActiveIndex, onKeyDown, listRef } = useKeyNav(suggestList, (d) => openDoctor(d), { onEscape: () => setQ("") });
  const load = async () => {
    try {
      const { data } = await api.get("/admin/business/doctors", { params: q ? { q } : {} });
      setRows(data.doctors || []);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { const t = setTimeout(load, 220); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const openDoctor = async (d) => {
    setActive(d);
    try { const { data } = await api.get(`/admin/business/doctor/${d.id}`); setDetail(data); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  const totals = useMemo(() => rows.reduce((acc, r) => ({
    appts: acc.appts + r.total_appointments,
    rev: acc.rev + r.revenue_generated,
    payouts: acc.payouts + r.payouts_done,
    pending: acc.pending + r.payouts_pending,
  }), { appts: 0, rev: 0, payouts: 0, pending: 0 }), [rows]);

  if (active && detail) {
    return (
      <div className="panel">
        <button className="back" onClick={() => { setActive(null); setDetail(null); }} data-testid="biz-doctor-back">
          <ArrowLeft size={15} /> Back to doctors
        </button>
        <div className="biz-detail-head">
          <div>
            <h3>{detail.doctor.name}</h3>
            <p className="sub">{detail.doctor.reg_no} · {detail.doctor.specialty} · {detail.doctor.email || "—"} · {detail.doctor.mobile || "—"}</p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditProfile(true)} data-testid="biz-doctor-edit" style={{ alignSelf: "flex-start" }}><Pencil size={13} /> Edit details</button>
        </div>
        <div className="biz-detail-stats" style={{ marginBottom: 4 }}>
          <div><div className="n">{detail.appointments.length}</div><div className="l">Total appts</div></div>
          <div><div className="n">{inr(detail.appointments.reduce((s, a) => s + (a.net_revenue || 0), 0))}</div><div className="l">Revenue generated</div></div>
          <div><div className="n">{inr(detail.payouts.filter(p => p.status === "completed").reduce((s, p) => s + p.amount, 0))}</div><div className="l">Payouts done</div></div>
        </div>
        <div className="biz-status-badges" data-testid="biz-doctor-status-badges">
          {["completed", "upcoming", "pending", "missed"].map(s => (
            <div className={`biz-status-badge ${s}`} key={s} data-testid={`biz-doctor-status-${s}`}>
              <span className="n">{(detail.status_counts || {})[s] || 0}</span>
              <span className="l">{s === "pending" ? "Pending (dr no-show)" : s === "missed" ? "Missed (pt no-show)" : s}</span>
            </div>
          ))}
        </div>
        <h4 className="biz-section-h">Services rendered</h4>
        <table data-testid="biz-doctor-appts">
          <thead><tr><th>Date</th><th>Patient</th><th>Service</th><th>Type</th><th>Status</th><th>Fee</th><th>Discount</th><th>Net</th></tr></thead>
          <tbody>{detail.appointments.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", padding: "24px 0", color: "var(--slate)" }}>No appointments yet.</td></tr>}{detail.appointments.map(a => (
            <tr key={a.id} className={a.kind === "peer" ? "row-peer" : ""}>
              <td>{fmtDate(a.date)} <small style={{ color: "var(--slate)" }}>{a.time}</small></td>
              <td>{a.patient_name}</td>
              <td>{a.consult_type}</td>
              <td><span className={`pill-mini ${a.kind === "peer" ? "peer" : ""}`}>{a.kind === "peer" ? "Peer" : "Patient"}</span></td>
              <td><span className={`pill-mini ${a.status}`}>{a.status}</span></td>
              <td>{a.kind === "peer" ? "—" : inr(a.fee)}</td>
              <td>{a.discount ? `−${inr(a.discount)}` : "—"}</td>
              <td><b>{a.kind === "peer" ? "—" : inr(a.net_revenue)}</b></td>
            </tr>
          ))}</tbody>
        </table>
        <h4 className="biz-section-h" style={{ marginTop: 24 }}>Payouts</h4>
        <table>
          <thead><tr><th>Date</th><th>Amount</th><th>Status</th><th>Notes</th></tr></thead>
          <tbody>{detail.payouts.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center", padding: "20px 0", color: "var(--slate)" }}>No payouts recorded.</td></tr>}{detail.payouts.map(p => (
            <tr key={p.id}><td>{fmtDate(p.date)}</td><td>{inr(p.amount)}</td><td><span className={`pill-mini ${p.status}`}>{p.status}</span></td><td>{p.notes || "—"}</td></tr>
          ))}</tbody>
        </table>
        {editProfile && (
          <DoctorEditModal doctor={detail.doctor} onClose={() => setEditProfile(false)} onSaved={() => { setEditProfile(false); openDoctor(active); load(); }} />
        )}
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <div><h3>Doctor performance</h3><p className="sub">{rows.length} doctor{rows.length === 1 ? "" : "s"} on the panel · Click any row to see services rendered and payout history.</p></div>
      </div>
      <div className="biz-stat-row">
        <div className="biz-stat"><div className="n">{rows.length}</div><div className="l">Doctors</div></div>
        <div className="biz-stat"><div className="n">{totals.appts}</div><div className="l">Patient appointments</div></div>
        <div className="biz-stat green"><div className="n">{inr(totals.rev)}</div><div className="l">Revenue from panel</div></div>
        <div className="biz-stat amber"><div className="n">{inr(totals.pending)}</div><div className="l">Pending payouts</div></div>
      </div>
      <div className="biz-search-wrap">
        <div className="patient-search-bar" style={{ maxWidth: 640, margin: 0 }}>
          <Search size={15} className="patient-search-ic" />
          <input
            className="patient-search-input"
            placeholder="Search by name, NMC number, mobile or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            aria-activedescendant={activeIndex >= 0 && suggestList[activeIndex] ? `biz-doctor-suggest-${suggestList[activeIndex].id}` : undefined}
            role="combobox"
            aria-expanded={suggestList.length > 0}
            data-testid="biz-doctors-search"
          />
          {q && <button className="patient-search-clear" onClick={() => setQ("")}>✕</button>}
        </div>
        {q.trim() !== "" && (
          <div className="biz-suggest" data-testid="biz-doctors-suggest" role="listbox" ref={listRef}>
            {rows.length === 0 && <div className="biz-suggest-empty">No doctors match “{q}”.</div>}
            {suggestList.map((d, i) => (
              <button
                key={d.id}
                type="button"
                id={`biz-doctor-suggest-${d.id}`}
                role="option"
                aria-selected={i === activeIndex}
                className={`biz-suggest-row ${i === activeIndex ? "active" : ""}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => openDoctor(d)}
                data-testid={`biz-doctor-suggest-${d.id}`}
              >
                <span className="ib-avatar doctor"><Stethoscope size={14} /></span>
                <span className="biz-suggest-main">
                  <b>{d.name}</b>
                  <small>{d.reg_no || "—"}{d.specialty ? " · " + d.specialty : ""}{d.email ? " · " + d.email : ""}</small>
                </span>
                <span className="biz-suggest-meta">{inr(d.revenue_generated)}</span>
              </button>
            ))}
            {rows.length > 10 && <div className="biz-suggest-more">+ {rows.length - 10} more · scroll the table below to see all matches</div>}
            {suggestList.length > 0 && <div className="biz-suggest-hint">↑ ↓ to move · ↵ to open · esc to clear</div>}
          </div>
        )}
      </div>
      <ChartToggle view={view} setView={setView} />
      {view === "table" && (
      <table data-testid="biz-doctors-table">
        <thead><tr><th>Sl.</th><th>Doctor</th><th>NMC</th><th>Specialty</th><th>Appts</th><th>Peer</th><th>Revenue</th><th>Paid out</th><th>Pending</th></tr></thead>
        <tbody>{rows.length === 0 && <tr><td colSpan={9} style={{ textAlign: "center", padding: "30px 0", color: "var(--slate)" }}>{q ? "No doctors match." : "No doctors yet."}</td></tr>}{rows.map((d, i) => (
          <tr key={d.id} className="row-click" onClick={() => openDoctor(d)} data-testid={`biz-doctor-row-${d.id}`}>
            <td>{i + 1}</td>
            <td><b>{d.name}</b><br /><small style={{ color: "var(--slate)" }}>{d.email || ""}</small></td>
            <td><span className="pid-chip">{d.reg_no || "—"}</span></td>
            <td>{d.specialty || "—"}</td>
            <td>{d.total_appointments} <small style={{ color: "var(--slate)" }}>({d.completed} done)</small></td>
            <td>{d.peer_consults}</td>
            <td><b>{inr(d.revenue_generated)}</b></td>
            <td>{inr(d.payouts_done)}</td>
            <td>{d.payouts_pending ? <span style={{ color: "var(--amber-ink)" }}>{inr(d.payouts_pending)}</span> : "—"}</td>
          </tr>
        ))}</tbody>
      </table>
      )}
      {view === "bar" && (() => {
        const data = [...rows].sort((a, b) => b.revenue_generated - a.revenue_generated).slice(0, 15).map(d => ({
          name: (d.name || "—").replace("Dr. ", ""),
          Revenue: d.revenue_generated,
          "Paid out": d.payouts_done,
          Pending: d.payouts_pending,
        }));
        if (data.every(d => d.Revenue === 0 && d["Paid out"] === 0 && d.Pending === 0))
          return <div className="biz-chart-empty">No revenue yet — chart will populate once patients book and complete consultations.</div>;
        return (
          <ChartFrame title="Top doctors by revenue (with payout status)">
            <BarChart data={data} margin={{ top: 10, right: 16, bottom: 60, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6eef7" />
              <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={70} stroke="#6e8398" fontSize={12} />
              <YAxis stroke="#6e8398" fontSize={12} tickFormatter={(v) => "₹" + v} />
              <Tooltip formatter={(v) => inr(v)} />
              <Legend />
              <Bar dataKey="Revenue" fill="#185FA5" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Paid out" fill="#1D9E75" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Pending" fill="#E0A82E" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ChartFrame>
        );
      })()}
      {view === "pie" && (() => {
        // Revenue share by specialty
        const map = new Map();
        rows.forEach(d => {
          const k = d.specialty || "Unspecified";
          map.set(k, (map.get(k) || 0) + d.revenue_generated);
        });
        const data = [...map.entries()].filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
        if (data.length === 0) return <div className="biz-chart-empty">No revenue recorded yet.</div>;
        return (
          <ChartFrame title="Revenue share by specialty">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={130} paddingAngle={2}>
                {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => inr(v)} />
              <Legend verticalAlign="bottom" />
            </PieChart>
          </ChartFrame>
        );
      })()}
    </div>
  );
}

/* ============================================================
   BUSINESS · REVENUE (financial ledger)
   ============================================================ */
const LEDGER_TYPES = [
  { v: "investment", label: "Investment received" },
  { v: "payout", label: "Doctor payout" },
  { v: "expense", label: "Operating expense" },
  { v: "tax", label: "Tax" },
  { v: "refund", label: "Patient refund" },
];
const titleCase = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const ledgerTypeLabel = (t, entries) => {
  const known = LEDGER_TYPES.find((x) => x.v === t);
  if (known) return known.label;
  const ent = (entries || []).find((e) => e.type === t && e.type_label);
  return ent?.type_label || titleCase(t);
};
const PARTY_KINDS = [
  { v: "doctor", label: "Doctor" },
  { v: "investor", label: "Investor" },
  { v: "vendor", label: "Vendor" },
  { v: "tax_authority", label: "Tax authority" },
  { v: "patient", label: "Patient" },
  { v: "other", label: "Other" },
];


function SubscriberDiscountCard() {
  const [data, setData] = useState(null);
  const [pct, setPct] = useState("");
  const [applies, setApplies] = useState("all");
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: r } = await api.get("/admin/settings/subscriber-discount");
      setData(r);
      setPct(String(r.percent ?? 10));
      setApplies(r.applies_to || "all");
      setEnabled(r.enabled !== false);
    } catch (e) { /* non-blocking */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const n = parseFloat(pct);
    if (Number.isNaN(n) || n < 0 || n > 100) {
      toast("Percent must be between 0 and 100"); return;
    }
    setBusy(true);
    try {
      await api.put("/admin/settings/subscriber-discount", {
        percent: n, applies_to: applies, enabled,
      });
      toast("Subscriber discount saved ✓");
      await load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  return (
    <div className="sub-discount-card" data-testid="sub-discount-card">
      <div className="sd-icon"><Crown size={22} /></div>
      <div>
        <h4>Premium subscriber discount</h4>
        <p>
          Automatically applied at checkout for every patient whose subscription is active. Set the percentage and which
          products it applies to. Changes are live immediately.
        </p>
      </div>
      <div className="sub-discount-form">
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#0F6E56" }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            data-testid="sub-discount-enabled"
          /> Enabled
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <input
            className="in"
            type="number" min="0" max="100" step="0.5"
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            data-testid="sub-discount-pct"
          />
          <span style={{ fontWeight: 700, color: "#0F6E56" }}>%</span>
        </div>
        <select
          className="in"
          value={applies}
          onChange={(e) => setApplies(e.target.value)}
          data-testid="sub-discount-applies"
        >
          <option value="all">All products</option>
          <option value="online_consult">Online consultation only</option>
          <option value="clinic_consult">Clinic consultation only</option>
        </select>
        <button
          className="btn btn-green btn-sm"
          onClick={save}
          disabled={busy}
          data-testid="sub-discount-save"
        >
          <BadgeCheck size={13} /> {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

export function BusinessRevenue() {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // entry object or {} for new
  const [view, setView] = useState("table");

  const loadAll = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/business/summary"),
        api.get("/admin/business/ledger"),
      ]);
      setSummary(s.data);
      setEntries(l.data.entries || []);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { loadAll(); }, []);

  const filtered = filter === "all" ? entries : entries.filter(e => e.type === filter);

  const remove = async (e) => {
    if (!window.confirm(`Delete this ${e.type} entry of ${inr(e.amount)}?`)) return;
    try { await api.delete(`/admin/business/ledger/${e.id}`); toast("Entry deleted ✓"); loadAll(); }
    catch (err) { toast(formatApiError(err.response?.data?.detail)); }
  };

  if (!summary) return <div className="panel"><p className="lead">Loading…</p></div>;

  const customTypes = [...new Set(entries.map((e) => e.type).filter((t) => !LEDGER_TYPES.some((x) => x.v === t)))];
  const customCards = customTypes.map((t) => {
    const done = entries.filter((e) => e.type === t && e.status === "completed").reduce((s, e) => s + e.amount, 0);
    const pend = entries.filter((e) => e.type === t && e.status === "pending").reduce((s, e) => s + e.amount, 0);
    return { ic: Receipt, label: ledgerTypeLabel(t, entries), v: inr(done), sub: pend ? `${inr(pend)} pending` : "Custom category", custom: true };
  });

  const cards = [
    { ic: BadgeIndianRupee, label: "Gross revenue", v: inr(summary.gross_revenue), sub: "All paid appointments" },
    { ic: TrendingUp, label: "Recognised revenue", v: inr(summary.recognised_revenue), sub: "Completed only", tone: "green" },
    { ic: BadgePercent, label: "Discounts given", v: inr(summary.discount_total), sub: `${summary.appointments_completed} done` },
    { ic: Banknote, label: "Investments received", v: inr(summary.investments_received), sub: summary.investments_pending ? `${inr(summary.investments_pending)} pending` : "—" },
    { ic: Wallet, label: "Payouts done", v: inr(summary.payouts_done), sub: summary.payouts_pending ? `${inr(summary.payouts_pending)} pending` : "—" },
    { ic: Receipt, label: "Operating expenses", v: inr(summary.operating_expenses), sub: "Vendors / infra / ops" },
    { ic: Receipt, label: "Taxes paid", v: inr(summary.taxes_paid), sub: "GST / TDS / IT" },
    { ic: Activity, label: "EBITDA", v: inr(summary.ebitda), sub: "Revenue − payouts − opex", tone: summary.ebitda >= 0 ? "green" : "amber" },
    { ic: Activity, label: "Net profit", v: inr(summary.net_profit), sub: "After taxes", tone: summary.net_profit >= 0 ? "green" : "amber" },
    ...customCards,
  ];

  return (
    <>
      <B2BRevenueTile />
      <SubscriberDiscountCard />
    <div className="panel">
      <div className="panel-head">
        <div>
          <h3>Revenue &amp; financial ledger</h3>
          <p className="sub">Live across the platform — revenue updates automatically when patients pay; you maintain investments, payouts, expenses and taxes manually below.</p>
        </div>
        <button className="btn btn-green" onClick={() => setEditing({})} data-testid="biz-ledger-add"><Plus size={15} /> Add ledger entry</button>
      </div>

      <div className="biz-grid">
        {cards.map((c, i) => (
          <div className={`biz-card ${c.tone || ""}`} key={i}>
            <c.ic size={18} className="biz-card-ic" />
            <div className="biz-card-v">{c.v}</div>
            <div className="biz-card-l">{c.label}</div>
            <div className="biz-card-s">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="biz-ledger-head">
        <h4 className="biz-section-h">Ledger entries</h4>
        <div className="biz-tabs">
          <button className={filter === "all" ? "on" : ""} onClick={() => setFilter("all")} data-testid="biz-filter-all">All</button>
          {LEDGER_TYPES.map(t => (
            <button key={t.v} className={filter === t.v ? "on" : ""} onClick={() => setFilter(t.v)} data-testid={`biz-filter-${t.v}`}>{t.label}</button>
          ))}
          {customTypes.map(t => (
            <button key={t} className={filter === t ? "on" : ""} onClick={() => setFilter(t)} data-testid={`biz-filter-${t}`}>{ledgerTypeLabel(t, entries)}</button>
          ))}
        </div>
      </div>

      <ChartToggle view={view} setView={setView} />
      {view === "table" && (
      <table data-testid="biz-ledger-table">
        <thead><tr><th>Date</th><th>Type</th><th>Flow</th><th>Counterparty</th><th>Amount</th><th>Status</th><th>Notes</th><th>Actions</th></tr></thead>
        <tbody>{filtered.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", padding: "30px 0", color: "var(--slate)" }}>No ledger entries{filter !== "all" ? ` of type "${filter}"` : ""} yet. Click <b>Add ledger entry</b> to record your first one.</td></tr>}{filtered.map(e => (
          <tr key={e.id} data-testid={`biz-ledger-row-${e.id}`}>
            <td>{fmtDate(e.date)}</td>
            <td><span className={`pill-mini type-${e.type}`}>{ledgerTypeLabel(e.type, entries)}</span></td>
            <td>{(() => { const dir = e.direction || DIR_DEFAULT[e.type] || "out"; return <span className={`dir-pill ${dir}`} data-testid={`biz-ledger-dir-${e.id}`}>{dir === "in" ? "↓ In" : "↑ Out"}</span>; })()}</td>
            <td>{e.party || "—"} <small style={{ color: "var(--slate)" }}>{e.party_kind && e.party_kind !== "other" ? `· ${e.party_kind}` : ""}</small></td>
            <td><b>{inr(e.amount)}</b></td>
            <td><span className={`pill-mini ${e.status}`}>{e.status}</span></td>
            <td><small style={{ color: "var(--slate)" }}>{e.notes || "—"}</small></td>
            <td>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(e)} data-testid={`biz-ledger-edit-${e.id}`}><Pencil size={13} /></button>
              <button className="btn btn-danger btn-sm" onClick={() => remove(e)} data-testid={`biz-ledger-del-${e.id}`}><Trash2 size={13} /></button>
            </td>
          </tr>
        ))}</tbody>
      </table>
      )}
      {view === "bar" && (() => {
        const data = [
          { name: "Recognised revenue", value: summary.recognised_revenue, fill: "#1D9E75" },
          { name: "Investments", value: summary.investments_received, fill: "#185FA5" },
          { name: "Payouts done", value: summary.payouts_done, fill: "#E0A82E" },
          { name: "Payouts pending", value: summary.payouts_pending, fill: "#D87B33" },
          { name: "Operating expenses", value: summary.operating_expenses, fill: "#604096" },
          { name: "Taxes paid", value: summary.taxes_paid, fill: "#A23D2C" },
          { name: "EBITDA", value: summary.ebitda, fill: summary.ebitda >= 0 ? "#1c6f4d" : "#A23D2C" },
          { name: "Net profit", value: summary.net_profit, fill: summary.net_profit >= 0 ? "#0D8AB7" : "#A23D2C" },
        ];
        return (
          <ChartFrame title="Money in & money out — live across the platform">
            <BarChart data={data} margin={{ top: 10, right: 20, bottom: 80, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6eef7" />
              <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={90} stroke="#6e8398" fontSize={12} />
              <YAxis stroke="#6e8398" fontSize={12} tickFormatter={(v) => "₹" + v} />
              <Tooltip formatter={(v) => inr(v)} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ChartFrame>
        );
      })()}
      {view === "pie" && (() => {
        // Ledger composition by type (completed only) + Recognised revenue
        const ledgerByType = {};
        entries.filter(e => e.status === "completed").forEach(e => {
          ledgerByType[e.type] = (ledgerByType[e.type] || 0) + e.amount;
        });
        const data = [];
        if (summary.recognised_revenue > 0) data.push({ name: "Recognised revenue", value: summary.recognised_revenue });
        LEDGER_TYPES.forEach(t => {
          const v = ledgerByType[t.v] || 0;
          if (v > 0) data.push({ name: t.label, value: v });
        });
        customTypes.forEach(t => {
          const v = ledgerByType[t] || 0;
          if (v > 0) data.push({ name: ledgerTypeLabel(t, entries), value: v });
        });
        if (data.length === 0) return <div className="biz-chart-empty">No financial activity yet. Add ledger entries or wait for completed appointments.</div>;
        return (
          <ChartFrame title="Cash flow composition (completed only)">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={130} paddingAngle={2}>
                {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => inr(v)} />
              <Legend verticalAlign="bottom" />
            </PieChart>
          </ChartFrame>
        );
      })()}

      {editing && (
        <LedgerModal entry={editing.id ? editing : null} customTypes={customTypes} entries={entries} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadAll(); }} />
      )}
    </div>
    </>
  );
}

const DIR_DEFAULT = { investment: "in", payout: "out", expense: "out", tax: "out", refund: "out" };

function LedgerModal({ entry, customTypes = [], entries = [], onClose, onSaved }) {
  const isCustom = entry && !LEDGER_TYPES.some((t) => t.v === entry.type);
  const [form, setForm] = useState({
    type: isCustom ? "__custom__" : (entry?.type || "investment"),
    customLabel: isCustom ? ledgerTypeLabel(entry.type, entries) : "",
    direction: entry?.direction || DIR_DEFAULT[entry?.type] || "in",
    amount: entry?.amount ?? "",
    party: entry?.party || "",
    party_id: entry?.party_id || "",
    party_kind: entry?.party_kind || "investor",
    status: entry?.status || "completed",
    date: entry?.date || new Date().toISOString().slice(0, 10),
    notes: entry?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onTypeChange = (v) => setForm((f) => ({ ...f, type: v, direction: DIR_DEFAULT[v] || f.direction }));

  // counterparty autocomplete from registered people
  const [cps, setCps] = useState([]);
  const [cpOpen, setCpOpen] = useState(false);
  useEffect(() => { api.get("/admin/business/counterparties").then((r) => setCps(r.data.counterparties || [])).catch(() => {}); }, []);
  const cpMatches = form.party.trim() === "" ? [] : cps.filter((c) => {
    const q = form.party.toLowerCase();
    return c.name.toLowerCase().includes(q) || (c.ref || "").toLowerCase().includes(q);
  }).slice(0, 8);
  const pickCp = (c) => { setForm((f) => ({ ...f, party: c.name, party_id: c.id, party_kind: c.kind })); setCpOpen(false); };
  const cpNav = useKeyNav(cpMatches, pickCp, { onEscape: () => setCpOpen(false) });

  const save = async () => {
    if (form.amount === "" || Number(form.amount) < 0) { toast("Please enter a valid amount."); return; }
    if (form.type === "__custom__" && !form.customLabel.trim()) { toast("Please name the custom category."); return; }
    setBusy(true);
    try {
      const customName = form.customLabel.trim();
      const body = {
        type: form.type === "__custom__" ? customName : form.type,
        type_label: form.type === "__custom__" ? customName : "",
        direction: form.direction,
        amount: Number(form.amount),
        party: form.party, party_id: form.party_id, party_kind: form.party_kind, status: form.status, date: form.date, notes: form.notes,
      };
      if (entry?.id) await api.patch(`/admin/business/ledger/${entry.id}`, body);
      else await api.post("/admin/business/ledger", body);
      toast(entry ? "Entry updated ✓" : "Entry added ✓");
      onSaved();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-shade" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h3>{entry ? "Edit ledger entry" : "Add ledger entry"}</h3>
        <div className="biz-form-grid">
          <div>
            <label className="ib-field-label">Type</label>
            <select className="ib-lookup-input" value={form.type} onChange={(e) => onTypeChange(e.target.value)} data-testid="biz-ledger-type">
              {LEDGER_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              {customTypes.map(t => <option key={t} value={t}>{ledgerTypeLabel(t, entries)}</option>)}
              <option value="__custom__">➕ New custom category…</option>
            </select>
          </div>
          {form.type === "__custom__" && (
            <div>
              <label className="ib-field-label">Custom category name</label>
              <input className="ib-lookup-input" value={form.customLabel} onChange={(e) => update("customLabel", e.target.value)} placeholder="e.g. Marketing spend, Grant received" data-testid="biz-ledger-custom-label" />
            </div>
          )}
          <div>
            <label className="ib-field-label">Direction</label>
            <select className="ib-lookup-input" value={form.direction} onChange={(e) => update("direction", e.target.value)} data-testid="biz-ledger-direction">
              <option value="in">Incoming fund (money in)</option>
              <option value="out">Outgoing fund (money out)</option>
            </select>
          </div>
          <div>
            <label className="ib-field-label">Amount (₹)</label>
            <input type="number" className="ib-lookup-input" value={form.amount} onChange={(e) => update("amount", e.target.value)} min={0} data-testid="biz-ledger-amount" />
          </div>
          <div className="ledger-cp-wrap">
            <label className="ib-field-label">Counterparty name</label>
            <input
              className="ib-lookup-input"
              value={form.party}
              onChange={(e) => { update("party", e.target.value); update("party_id", ""); setCpOpen(true); }}
              onFocus={() => setCpOpen(true)}
              onKeyDown={cpNav.onKeyDown}
              placeholder="Type to search registered people…"
              autoComplete="off"
              role="combobox"
              aria-expanded={cpOpen && cpMatches.length > 0}
              data-testid="biz-ledger-party"
            />
            {cpOpen && cpMatches.length > 0 && (
              <div className="ledger-cp-suggest" role="listbox" ref={cpNav.listRef} data-testid="biz-ledger-party-suggest">
                {cpMatches.map((c, i) => (
                  <button
                    type="button"
                    key={c.id}
                    role="option"
                    aria-selected={i === cpNav.activeIndex}
                    className={`ledger-cp-row ${i === cpNav.activeIndex ? "active" : ""}`}
                    onMouseEnter={() => cpNav.setActiveIndex(i)}
                    onClick={() => pickCp(c)}
                    data-testid={`biz-ledger-party-opt-${c.id}`}
                  >
                    {c.kind === "doctor" ? <Stethoscope size={14} /> : <UserRound size={14} />}
                    <span>
                      <b>{c.name}</b><br />
                      <small>{c.kind === "doctor" ? "Doctor" : "Patient"}{c.ref ? ` · ${c.ref}` : ""}{c.detail ? ` · ${c.detail}` : ""}</small>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="ib-field-label">Counterparty kind</label>
            <select className="ib-lookup-input" value={form.party_kind} onChange={(e) => update("party_kind", e.target.value)} data-testid="biz-ledger-party-kind">
              {PARTY_KINDS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="ib-field-label">Status</label>
            <select className="ib-lookup-input" value={form.status} onChange={(e) => update("status", e.target.value)} data-testid="biz-ledger-status">
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div>
            <label className="ib-field-label">Date</label>
            <input type="date" className="ib-lookup-input" value={form.date} onChange={(e) => update("date", e.target.value)} data-testid="biz-ledger-date" />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label className="ib-field-label">Notes</label>
            <input className="ib-lookup-input" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Optional details" data-testid="biz-ledger-notes" />
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-green" onClick={save} disabled={busy} data-testid="biz-ledger-save">{busy ? "Saving…" : "Save entry"}</button>
        </div>
      </div>
    </div>
  );
}


/* ============================================================
   PROFILE EDIT MODALS (Business section is fully editable,
   except Patient ID and Doctor NMC which are permanent).
   ============================================================ */
function PatientEditModal({ patient, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: patient?.name || "",
    email: patient?.email || "",
    mobile: patient?.mobile || "",
  });
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    if (!form.name.trim()) { toast("Name is required."); return; }
    setBusy(true);
    try {
      await api.put(`/admin/patients/${patient.id}`, form);
      toast("Patient details updated ✓");
      onSaved();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-shade" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h3>Edit patient details</h3>
        <div className="biz-form-grid">
          <div style={{ gridColumn: "1 / -1" }}>
            <label className="ib-field-label">Patient ID (permanent)</label>
            <input className="ib-lookup-input" value={patient.patient_code} disabled data-testid="biz-patient-edit-id" style={{ background: "#eef2f7", color: "#6b7c8f" }} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label className="ib-field-label">Full name</label>
            <input className="ib-lookup-input" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="biz-patient-edit-name" />
          </div>
          <div>
            <label className="ib-field-label">Email</label>
            <input className="ib-lookup-input" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="biz-patient-edit-email" />
          </div>
          <div>
            <label className="ib-field-label">Mobile</label>
            <input className="ib-lookup-input" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} data-testid="biz-patient-edit-mobile" />
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-green" onClick={save} disabled={busy} data-testid="biz-patient-edit-save">{busy ? "Saving…" : "Save details"}</button>
        </div>
      </div>
    </div>
  );
}

function DoctorEditModal({ doctor, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: doctor?.name || "",
    specialty: doctor?.specialty || "",
    email: doctor?.email || "",
    mobile: doctor?.mobile || "",
  });
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    if (!form.name.trim()) { toast("Name is required."); return; }
    setBusy(true);
    try {
      await api.put(`/admin/doctors/${doctor.id}/profile`, form);
      toast("Doctor details updated ✓");
      onSaved();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-shade" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h3>Edit doctor details</h3>
        <div className="biz-form-grid">
          <div style={{ gridColumn: "1 / -1" }}>
            <label className="ib-field-label">NMC number (permanent)</label>
            <input className="ib-lookup-input" value={doctor.reg_no} disabled data-testid="biz-doctor-edit-nmc" style={{ background: "#eef2f7", color: "#6b7c8f" }} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label className="ib-field-label">Full name</label>
            <input className="ib-lookup-input" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="biz-doctor-edit-name" />
          </div>
          <div>
            <label className="ib-field-label">Specialty</label>
            <input className="ib-lookup-input" value={form.specialty} onChange={(e) => update("specialty", e.target.value)} data-testid="biz-doctor-edit-specialty" />
          </div>
          <div>
            <label className="ib-field-label">Email</label>
            <input className="ib-lookup-input" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="biz-doctor-edit-email" />
          </div>
          <div>
            <label className="ib-field-label">Mobile</label>
            <input className="ib-lookup-input" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} data-testid="biz-doctor-edit-mobile" />
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-green" onClick={save} disabled={busy} data-testid="biz-doctor-edit-save">{busy ? "Saving…" : "Save details"}</button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   BUSINESS · PAYMENTS  (stub — details to be built later)
   ============================================================ */
export function BusinessPayments() {
  return (
    <div className="panel" data-testid="biz-payments-page">
      <div className="panel-head">
        <div>
          <h3>Payments</h3>
          <p className="sub">Make payouts to doctors and issue refunds to patients. Every payment recorded here will post automatically to the Revenue ledger.</p>
        </div>
      </div>
      <div className="biz-payments-empty" data-testid="biz-payments-coming-soon">
        <Wallet size={34} strokeWidth={1.5} />
        <h4>Payments workspace coming soon</h4>
        <p>This is where you'll initiate <b>doctor payouts</b> and <b>patient refunds</b>, track their status, and have each one flow straight into the financial ledger as an outgoing entry. We'll build out the full workflow next.</p>
      </div>
    </div>
  );
}

